import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { HealthData } from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fetchHealth } from "@/shared/api/health";

export function HomePage() {
  const [health, setHealth] = useState<HealthData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchHealth()
      .then((data) => {
        if (!cancelled) setHealth(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "API unreachable");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="max-w-xl animate-[softRise_420ms_ease-out]">
      <h1 className="m-0 font-[family-name:var(--font-display)] text-4xl font-semibold tracking-tight text-[var(--color-foreground)] sm:text-5xl">
        iCraftX
      </h1>
      <p className="mt-3 max-w-md text-sm leading-relaxed text-[var(--color-muted)]">
        Minimalist ERP — sign in to open a company.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Button asChild>
          <Link to="/login" className="no-underline">
            Login
          </Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/select-company" className="no-underline">
            Companies
          </Link>
        </Button>
      </div>
      <section
        className="mt-12 border-t border-[var(--color-border)] pt-6"
        aria-live="polite"
      >
        <h2 className="m-0 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-fg)]">
          API
        </h2>
        {health && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            <Badge variant="success">{health.status}</Badge>
            <span className="text-[var(--color-muted)]">
              {health.service} · {health.version}
              {health.databaseConfigured != null &&
                ` · DB ${health.databaseConfigured ? "configured" : "not set"}`}
            </span>
          </div>
        )}
        {error && (
          <Alert variant="destructive" className="mt-3">
            {error}
          </Alert>
        )}
        {!health && !error && (
          <p className="mt-3 text-sm text-[var(--color-muted)]">
            Checking /v1/health…
          </p>
        )}
      </section>
    </div>
  );
}
